import type { StyleElements } from "replo-runtime/shared/utils/styles";
import type { CustomPropsRecord } from "schemas/component";
import type { ComponentMovementSource } from "../../../shared/utils/dragging";
import type { ComponentConfig } from "../../components";

import { convertToLegacyProps } from "../../../shared/utils/props";

function getConfigurableProps(): CustomPropsRecord {
  return {
    _option: {
      name: "Option",
      type: "productOptions",
      defaultValue: null,
    },
    _swatches: {
      type: "swatches",
      name: "Swatches",
      description: "",
      defaultValue: null,
    },
    _showOptionsNotSoldTogether: {
      name: "Show Options Not Sold Together",
      type: "boolean",
      description:
        "Show all options, even if they are not sold together with the other selected options",
      defaultValue: false,
    },
    _accessibilityHidden: {
      type: "boolean",
      name: "Accessibility Hidden",
      description:
        "If set, removes this component from the accessibility tree, hiding it from screen readers.",
      defaultValue: null,
    },
    _accessibilityLabelledBy: {
      type: "selectable",
      name: "Accessibility Labelled By",
      description:
        "If you select a Text component here, the Text component will be used to label this component in accessibility controls.",
      defaultValue: null,
      placeholder: "Select a component",
    },
    _accessibilityLabel: {
      type: "string",
      name: "Accessibility Label",
      description:
        "If provided, this will be used as the aria-label for this component. Defaults to the option name.",
      defaultValue: undefined,
    },
  };
}

const styleElements = {
  root: {
    defaultStyles: {
      display: "flex",
    },
  },
} as const satisfies StyleElements;

const config = {
  renderData: {
    newInstancesUseNumbering: true,
    customProps: convertToLegacyProps(getConfigurableProps()),
    acceptsArbitraryChildren: (config) => {
      // Note (Noah, 2023-12-22, REPL-9838): We can accept arbitrary children only
      // if the child being dragged in is NOT a template. This is because it's too
      // easy to accidentally drag a component in as a child of the list, which will
      // mess up the list's rendering in a way you almost always don't want.
      return ![
        "leftBarTemplate" satisfies ComponentMovementSource,
        "canvas" satisfies ComponentMovementSource,
      ].includes(config.movementSource);
    },
    isAlwaysDynamic: true,
    canvasIndicatorDragDirections: [],
    ancestorAllow: {
      ancestorTypes: ["product"],
      message:
        "Option List components may only be placed inside a Product box.",
    },
    ancestorDisallowList: [
      {
        ancestorTypes: ["marquee"],
        message: "Option Lists cannot be nested inside tickers.",
      },
    ],
    allowsLayoutModification: true,
    canContainChildren: true,
    showComponentControlsFromChildren: true,
    showAccessibilityMenu: true,
    styleElements,
    variantTriggers: [
      "state.product.isOptionValueUnavailable",
      "state.product.selectedOptionValues",
    ],
  },
} satisfies ComponentConfig;

export default config;
